import { useDispatch } from "react-redux";
import { useEffect, useState } from "react";
import { Dialog } from "@mui/material";
import { AppDispatch } from "app/store";
import { actions } from "app/configSlice";
import { updateParentConfig } from "app/api";
import { btnDelay, usePlaySound, useScale } from "common/utils";
import { ParentCloseBtn, FadeTransition } from "common/elements";
import i18n, { setParentLocale } from "features/parent/i18n";

type ParentLocaleOption = "ja_kanji" | "en" | "zh_tw";

interface ParentLanguageEditModalProps {
  open: boolean;
  currentLocale: string | null | undefined;
  parentUid: string;
  onClose: () => void;
}

const normalizeLocale = (locale?: string | null): ParentLocaleOption => {
  if (!locale || locale === "ja") return "ja_kanji";
  if (locale === "zh_cn") return "zh_tw";
  if (locale === "en" || locale === "zh_tw" || locale === "ja_kanji") {
    return locale;
  }
  return "ja_kanji";
};

export const ParentLanguageEditModal = ({
  open,
  currentLocale,
  parentUid,
  onClose,
}: ParentLanguageEditModalProps) => {
  const play = usePlaySound();
  const { scale } = useScale();
  const dispatch = useDispatch<AppDispatch>();
  const [locale, setLocale] = useState<ParentLocaleOption>(normalizeLocale(currentLocale));

  useEffect(() => {
    if (open) {
      setLocale(normalizeLocale(currentLocale));
    }
  }, [open, currentLocale]);

  const handleConfirm = () => {
    play();
    btnDelay(() => {
      dispatch(actions.updateParentConfig({ key: "locale", value: locale }));
      dispatch(updateParentConfig({ uid: parentUid }));
      setParentLocale(locale);
      onClose();
    });
  };

  const localeOptions: { value: ParentLocaleOption; label: string }[] = [
    { value: "ja_kanji", label: i18n.t("MSG_PARENT_PAGE_LANGUAGE_JA") },
    { value: "en", label: i18n.t("MSG_PARENT_PAGE_LANGUAGE_EN") },
    { value: "zh_tw", label: i18n.t("MSG_PARENT_PAGE_LANGUAGE_ZH_TW") },
  ];

  return (
    <Dialog
      fullScreen
      open={open}
      maxWidth={false}
      sx={{ zIndex: 99999 }}
      TransitionComponent={FadeTransition}
      PaperProps={{ style: { backgroundColor: "transparent" } }}
    >
      <div className="flex-col-center w-full h-full bg-textcolor-black/50">
        <div style={{ transform: `scale(${scale})` }}>
          <div className="flex-col-el w-[510px] border-solid border-gray2-30 border-[16px] rounded-[16px] bg-gray2-30">
            <div className="flex-col-el justify-start w-full h-full relative p-6 rounded-[8px] bg-white">
              <ParentCloseBtn close={onClose} />
              <div className="flex-row-el items-center gap-2 mb-4 pb-3 border-b border-outline shrink-0">
                <span className="material-symbols-outlined !text-[54px] text-red-100">
                  language
                </span>
                <h2 className="text-[33px] text-textcolor-black">
                  {i18n.t("MSG_PARENT_PAGE_LANGUAGE_EDIT_TITLE")}
                </h2>
              </div>
              <div className="flex-col-view items-start py-4">
                <p className="text-body text-textcolor-black text-[24px]">
                  {i18n.t("MSG_PARENT_PAGE_LANGUAGE_EDIT_MESSAGE")}
                </p>

                <div className="flex-col-el w-full gap-3 my-6">
                  {localeOptions.map((option) => (
                    <button
                      key={option.value}
                      onClick={() => {
                        play();
                        setLocale(option.value);
                      }}
                      className={`flex-row-el items-center gap-3 w-full p-4 rounded-[12px] border-2 transition-all ${
                        locale === option.value
                          ? "bg-gray2-20 border-ink"
                          : "bg-white border-transparent hover:bg-surface"
                      }`}
                    >
                      <div
                        className={`w-6 h-6 rounded-full border-2 flex items-center justify-center ${
                          locale === option.value ? "border-ink" : "border-gray2-60"
                        }`}
                      >
                        {locale === option.value && <div className="w-3 h-3 rounded-full bg-ink" />}
                      </div>
                      <span className="text-[27px] text-textcolor-black">{option.label}</span>
                    </button>
                  ))}
                </div>
              </div>

              <div className="flex-row-el flex-center gap-4">
                <button
                  onClick={() => {
                    play();
                    btnDelay(() => onClose());
                  }}
                  className="flex-row-el items-center justify-center w-[160px] py-3 bg-white border border-gray2-60 text-textcolor-black rounded-[12px] text-[24px] cursor-pointer hover:bg-gray2-20 transition-all"
                >
                  {i18n.t("MSG_CANCEL_BTN_BY_PARENT")}
                </button>
                <button
                  onClick={handleConfirm}
                  className="flex-row-el items-center justify-center w-[160px] py-3 bg-ink text-white border-none rounded-[12px] text-[24px] cursor-pointer hover:opacity-90 transition-all"
                >
                  {i18n.t("MSG_CONFIRM_BTN_BY_PARENT")}
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>
    </Dialog>
  );
};
